// IndexedDB によるローカルストレージ
// CLAUDE.md S10: 認証が必要な API レスポンスはキャッシュしない（ここで扱うのは Notion 同期データのみ）

const DB_NAME = 'lectio';
const DB_VERSION = 5;

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('books')) {
        const s = db.createObjectStore('books', { keyPath: 'id' });
        s.createIndex('updatedAt', 'updatedAt');
        s.createIndex('genre', 'genre', { multiEntry: true });
      }
      if (!db.objectStoreNames.contains('journals')) {
        db.createObjectStore('journals', { keyPath: 'yyyymm' });
      }
      if (!db.objectStoreNames.contains('aiCache')) {
        const s = db.createObjectStore('aiCache', { keyPath: 'key' });
        s.createIndex('createdAt', 'createdAt');
      }
      if (!db.objectStoreNames.contains('wishlist')) {
        const s = db.createObjectStore('wishlist', { keyPath: 'id' });
        s.createIndex('addedAt', 'addedAt');
      }
      if (!db.objectStoreNames.contains('aiHistory')) {
        const s = db.createObjectStore('aiHistory', { keyPath: 'id', autoIncrement: true });
        s.createIndex('createdAt', 'createdAt');
        s.createIndex('type', 'type');
      }
      if (!db.objectStoreNames.contains('embeddings')) {
        db.createObjectStore('embeddings', { keyPath: 'bookId' });
      }
      if (!db.objectStoreNames.contains('highlights')) {
        const s = db.createObjectStore('highlights', { keyPath: 'id', autoIncrement: true });
        s.createIndex('bookId', 'bookId');
        s.createIndex('createdAt', 'createdAt');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(storeNames, mode = 'readonly') {
  return openDB().then((db) => db.transaction(storeNames, mode));
}

function promisify(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ============ books ============
export async function upsertBook(book) {
  const t = await tx('books', 'readwrite');
  await promisify(t.objectStore('books').put(book));
}

export async function upsertBooks(books) {
  const t = await tx('books', 'readwrite');
  const store = t.objectStore('books');
  await Promise.all(books.map((b) => promisify(store.put(b))));
}

export async function getBook(id) {
  const t = await tx('books');
  return promisify(t.objectStore('books').get(id));
}

export async function getAllBooks() {
  const t = await tx('books');
  const all = await promisify(t.objectStore('books').getAll());
  return all || [];
}

export async function deleteBook(id) {
  const t = await tx('books', 'readwrite');
  await promisify(t.objectStore('books').delete(id));
}

export async function clearBooks() {
  const t = await tx('books', 'readwrite');
  await promisify(t.objectStore('books').clear());
}

// ============ journals ============
export async function saveJournal(yyyymm, content) {
  const t = await tx('journals', 'readwrite');
  await promisify(
    t.objectStore('journals').put({ yyyymm, content, createdAt: Date.now() })
  );
}

export async function getJournal(yyyymm) {
  const t = await tx('journals');
  return promisify(t.objectStore('journals').get(yyyymm));
}

export async function getAllJournals() {
  const t = await tx('journals');
  const all = await promisify(t.objectStore('journals').getAll());
  return all || [];
}

// ============ aiCache（短命なAI応答キャッシュ） ============
const AI_CACHE_TTL = 1000 * 60 * 60 * 24 * 7; // 7日

export async function getAiCache(key) {
  const t = await tx('aiCache');
  const entry = await promisify(t.objectStore('aiCache').get(key));
  if (!entry) return null;
  if (Date.now() - entry.createdAt > AI_CACHE_TTL) {
    deleteAiCache(key).catch(() => {});
    return null;
  }
  return entry.body;
}

export async function setAiCache(key, body) {
  const t = await tx('aiCache', 'readwrite');
  await promisify(
    t.objectStore('aiCache').put({ key, body, createdAt: Date.now() })
  );
}

export async function deleteAiCache(key) {
  const t = await tx('aiCache', 'readwrite');
  await promisify(t.objectStore('aiCache').delete(key));
}

// ============ embeddings（セマンティック検索用ベクトル） ============
export async function saveEmbedding(bookId, vector, sourceHash) {
  const t = await tx('embeddings', 'readwrite');
  await promisify(
    t.objectStore('embeddings').put({ bookId, vector, sourceHash, updatedAt: Date.now() })
  );
}

export async function getEmbedding(bookId) {
  const t = await tx('embeddings');
  return promisify(t.objectStore('embeddings').get(bookId));
}

export async function getAllEmbeddings() {
  const t = await tx('embeddings');
  const all = await promisify(t.objectStore('embeddings').getAll());
  return all || [];
}

export async function clearEmbeddings() {
  const t = await tx('embeddings', 'readwrite');
  await promisify(t.objectStore('embeddings').clear());
}

// ============ highlights（メモ内ハイライト = 紙のマーカー相当） ============
export async function addHighlight(bookId, text) {
  const t = await tx('highlights', 'readwrite');
  return promisify(
    t.objectStore('highlights').add({ bookId, text, createdAt: Date.now() })
  );
}

export async function deleteHighlight(id) {
  const t = await tx('highlights', 'readwrite');
  await promisify(t.objectStore('highlights').delete(id));
}

export async function getHighlightsByBook(bookId) {
  const t = await tx('highlights');
  const idx = t.objectStore('highlights').index('bookId');
  const all = await promisify(idx.getAll(bookId));
  return (all || []).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
}

export async function getAllHighlights() {
  const t = await tx('highlights');
  const all = await promisify(t.objectStore('highlights').getAll());
  return (all || []).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

// ============ aiHistory（AI 実行履歴） ============
// type: 'recommend' | 'keyword-search' | 'contrast' | 'serendipity' | 'map' | 'lineage' | 'course' | 'journal' | 'analyze' | 'ask'
// title: 一覧表示用の短いタイトル
// context: 入力情報（テーマ・対象書籍・哲学者など。再表示時に使う）
// payload: 結果データ（再表示時に使う）
export async function saveAiHistory(entry) {
  const t = await tx('aiHistory', 'readwrite');
  const id = await promisify(
    t.objectStore('aiHistory').add({ ...entry, createdAt: Date.now() })
  );
  return id;
}

export async function getAllAiHistory() {
  const t = await tx('aiHistory');
  const all = await promisify(t.objectStore('aiHistory').getAll());
  return (all || []).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function getAiHistoryByType(type) {
  const all = await getAllAiHistory();
  return all.filter((x) => x.type === type);
}

export async function getAiHistoryById(id) {
  const t = await tx('aiHistory');
  return promisify(t.objectStore('aiHistory').get(id));
}

export async function deleteAiHistory(id) {
  const t = await tx('aiHistory', 'readwrite');
  await promisify(t.objectStore('aiHistory').delete(id));
}

// チャットセッションのメッセージ追記（既存エントリの payload.messages を更新）
export async function appendChatMessage(historyId, message) {
  const t = await tx('aiHistory', 'readwrite');
  const store = t.objectStore('aiHistory');
  const existing = await promisify(store.get(historyId));
  if (!existing) return null;
  existing.payload = existing.payload || { messages: [] };
  existing.payload.messages = existing.payload.messages || [];
  existing.payload.messages.push(message);
  await promisify(store.put(existing));
  return existing;
}

// チャットセッションのタイトルを後から更新
export async function updateAiHistory(historyId, patch) {
  const t = await tx('aiHistory', 'readwrite');
  const store = t.objectStore('aiHistory');
  const existing = await promisify(store.get(historyId));
  if (!existing) return null;
  Object.assign(existing, patch);
  await promisify(store.put(existing));
  return existing;
}

// ============ wishlist（購入予定リスト） ============
// id は `${title}::${author}` のハッシュ風 slug
export function wishlistId(title, author) {
  return `${(title || '').trim()}::${(author || '').trim()}`;
}

export async function addToWishlist(item) {
  const id = item.id || wishlistId(item.title, item.author);
  const t = await tx('wishlist', 'readwrite');
  await promisify(
    t.objectStore('wishlist').put({ ...item, id, addedAt: Date.now() })
  );
  return id;
}

export async function removeFromWishlist(id) {
  const t = await tx('wishlist', 'readwrite');
  await promisify(t.objectStore('wishlist').delete(id));
}

export async function isInWishlist(id) {
  const t = await tx('wishlist');
  return !!(await promisify(t.objectStore('wishlist').get(id)));
}

export async function getWishlistItem(id) {
  const t = await tx('wishlist');
  return promisify(t.objectStore('wishlist').get(id));
}

export async function getAllWishlist() {
  const t = await tx('wishlist');
  const all = await promisify(t.objectStore('wishlist').getAll());
  return (all || []).sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
}

// ============ ローカル限定書籍（Notion 同期で消えない） ============
// wishlist から「購入済み」した本を積読として保存する用途。
// books ストアに id=`local-<uuid>` で保存し、Notion 同期 (upsertBooks) は put 操作なので
// ID が衝突しない限りこれらは保持される。
export function localBookId() {
  const u =
    (typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
  return `local-${u}`;
}

export async function addLocalBook({ name, author, genre, summary, learnings, reason, fromWishlistId, acquisition }) {
  const id = localBookId();
  const now = new Date().toISOString();
  const note = buildLocalBookNote({ summary, learnings, reason });
  const book = {
    id,
    name: (name || '').trim(),
    author: (author || '').trim(),
    status: '積読',
    genre: Array.isArray(genre) ? genre : genre ? [genre] : [],
    rating: '',
    startDate: '',
    endDate: '',
    createdAt: now,
    updatedAt: now,
    note,
    localOnly: true,
    fromWishlistId: fromWishlistId || '',
    acquisition: acquisition || '',
  };
  await upsertBook(book);
  return book;
}

function buildLocalBookNote({ summary, learnings, reason }) {
  const lines = [];
  if (summary) lines.push(summary);
  if (Array.isArray(learnings) && learnings.length) {
    lines.push('');
    lines.push('## 学べること');
    for (const l of learnings) lines.push(`- ${l}`);
  }
  if (reason) {
    lines.push('');
    lines.push('## 推薦理由');
    lines.push(reason);
  }
  return lines.join('\n');
}

// ============ localStorage helpers ============
export const ls = {
  getLastSync: () => parseInt(localStorage.getItem('lectio.lastSync') || '0', 10),
  setLastSync: (ts) => localStorage.setItem('lectio.lastSync', String(ts)),
  getPhiloSelection: () => {
    try {
      return JSON.parse(localStorage.getItem('lectio.philoSelection') || '{}');
    } catch {
      return {};
    }
  },
  setPhiloSelection: (obj) => localStorage.setItem('lectio.philoSelection', JSON.stringify(obj)),
};
